// Pharmacy 360 Analytics Dashboard

import { useState } from "react";
import { Header } from "@/components/universal/header";
import {
  ParamDataTableToolbar,
  ParamDateFilter,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import PharmacyMetricCards from "./-components/metric-cards";
import SubordersOverTime from "./-components/suborders-over-time";
import SubordersByPractice from "./-components/suborders-by-practice";
import TopProducts from "./-components/top-products";
import type { GetPharmacyKpisParams } from "@/requests/gen/react-query/fastAPI.schemas";
import {
  ProductTypeEnum,
  TimePeriodEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { useOnlyEntity } from "@/hooks/use-only-entity";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { keepPreviousData } from "@tanstack/react-query";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { parseAsString, parseAsStringEnum, useQueryStates } from "nuqs";
import TimePeriodDropdown from "@/components/ui/time-period-dropdown";
import { getAutoTimePeriod } from "@/lib/time-period";
import { useDefaultEntity } from "@/hooks/use-default-entity";
import { useEffectiveDateRange } from "@/hooks/use-effective-date-range";
import { getThisMonth } from "@/lib/get-date-ranges";
import { createFileRoute } from "@tanstack/react-router";
import { AssistantFilterBar } from "@/components/universal/filter-assistant/filter-bar";
import {
  dateRangeWithGroupingField,
  entityLookupField,
  productTypesField,
  timePeriodField,
} from "@/components/universal/filter-assistant/presets";

export const Route = createFileRoute(
  "/_private/_pharmacy-360/pharmacy360-analytics/",
)({
  component: PharmacyDashboard,
});

function PharmacyDashboard() {
  const [queryStates, setQueryStates] = useQueryStates({
    ...dataTableSearchParsers,
    created_at_start: parseAsString,
    created_at_end: parseAsString,
    time_period: parseAsStringEnum(Object.values(TimePeriodEnum)),
  });

  const onlyEntity = useOnlyEntity();
  const [pharmacySearch, setPharmacySearch] = useState<string | undefined>();
  const [practiceSearch, setPracticeSearch] = useState<string | undefined>();

  const {
    effectiveDateStart: effectiveCreatedAtStart,
    effectiveDateEnd: effectiveCreatedAtEnd,
    effectiveTimePeriod,
    markDateFilterTouched,
  } = useEffectiveDateRange({
    dateStart: queryStates.created_at_start,
    dateEnd: queryStates.created_at_end,
    timePeriod: queryStates.time_period,
  });

  const entityIds = onlyEntity ? [onlyEntity] : queryStates.entity_ids;

  const pharmacies = useGetEntitiesInfinite(
    {
      search: pharmacySearch,
      include_ids: entityIds,
      entity_types: ["Pharmacy"],
      services: ["pharmacy_360"],
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  const practices = useGetEntitiesInfinite(
    {
      search: practiceSearch,
      entity_ids: entityIds,
      include_visible_entities: "seen_by_entities",
      include_ids: queryStates.practice_ids,
      entity_types: ["Practice"],
      services: ["patient_360"],
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  // Extract params from URL search parameters
  const params: GetPharmacyKpisParams = {
    entity_ids: entityIds,
    practice_ids: queryStates.practice_ids,
    product_types: queryStates.product_types,
    created_at_start: effectiveCreatedAtStart,
    created_at_end: effectiveCreatedAtEnd,
    time_period: effectiveTimePeriod,
  };

  useDefaultEntity({
    entities: pharmacies.data?.pages.flatMap((p) => p.items),
    selected: queryStates.entity_ids,
    setSelected: (ids) => setQueryStates({ entity_ids: ids }),
    disabled: !!onlyEntity,
    getEntityId: (pharmacy) => pharmacy.entity_id,
  });

  // RENDER PAGE
  return (
    <div className="pb-4">
      <title>Pharmacy Analytics - Legrande Health Portal</title>
      <Header
        crumbs={[
          {
            name: "Analytics Dashboards",
            link: { to: "/pharmacy360-analytics" },
          },
        ]}
      />
      <AssistantFilterBar
        fields={[
          dateRangeWithGroupingField({
            name: "created_at",
            description: "The order creation date range to show.",
            current: {
              start: effectiveCreatedAtStart,
              end: effectiveCreatedAtEnd,
            },
            onApply: ({ start, end }, autoTimePeriod) =>
              setQueryStates({
                created_at_start: start,
                created_at_end: end,
                time_period: autoTimePeriod,
              }),
          }),
          ...(onlyEntity
            ? []
            : [
                entityLookupField({
                  name: "entity_ids",
                  entityType: "Pharmacy",
                  summary: "The pharmacy(ies) whose activity is shown",
                  currentIds: queryStates.entity_ids,
                  getLabel: (id) =>
                    pharmacies.data?.pages
                      .flatMap((p) => p.items)
                      .find((p) => p.entity_id === id)?.name,
                  onApply: (ids) => setQueryStates({ entity_ids: ids }),
                }),
              ]),
          entityLookupField({
            name: "practice_ids",
            entityType: "Practice",
            summary: "The practice(s) to filter by",
            currentIds: queryStates.practice_ids,
            getLabel: (id) =>
              practices.data?.pages
                .flatMap((p) => p.items)
                .find((p) => p.entity_id === id)?.name,
            onApply: (ids) => setQueryStates({ practice_ids: ids }),
          }),
          productTypesField({
            current: queryStates.product_types,
            onApply: (product_types) => setQueryStates({ product_types }),
          }),
          timePeriodField({
            current: effectiveTimePeriod,
            onApply: (time_period) => setQueryStates({ time_period }),
          }),
        ]}
      >
        <ParamDataTableToolbar alwaysVisibleCount={0} overflowCount={Infinity}>
          {/* Date filter */}
          <ParamDateFilter
            title="Created At"
            name="created_at"
            defaultValue={getThisMonth()}
            onDateChange={(dateRange) => {
              markDateFilterTouched();
              if (dateRange?.from && dateRange?.to) {
                setQueryStates({
                  time_period: getAutoTimePeriod(dateRange.from, dateRange.to),
                });
              }
            }}
          />

          {/* Entity dropdown */}
          {!onlyEntity && (
            <DropdownFilter
              title="Pharmacy"
              options={[
                {
                  items:
                    pharmacies.data?.pages
                      .flatMap((page) => page.items)
                      .map((pharmacy) => {
                        return {
                          label: pharmacy.name,
                          value: pharmacy.entity_id,
                        };
                      }) ?? [],
                },
              ]}
              values={queryStates.entity_ids}
              onChange={(values) => setQueryStates({ entity_ids: values })}
              search={pharmacySearch}
              setSearch={setPharmacySearch}
              isFetching={pharmacies.isFetchingNextPage}
              fetchNextPage={pharmacies.fetchNextPage}
            />
          )}

          {/* Practice dropdown */}
          <DropdownFilter
            title="Practice"
            options={[
              {
                items:
                  practices.data?.pages
                    .flatMap((page) => page.items)
                    .map((practice) => {
                      return {
                        label: practice.name,
                        value: practice.entity_id,
                      };
                    }) ?? [],
              },
            ]}
            values={queryStates.practice_ids}
            onChange={(values) => setQueryStates({ practice_ids: values })}
            search={practiceSearch}
            setSearch={setPracticeSearch}
            isFetching={practices.isFetchingNextPage}
            fetchNextPage={practices.fetchNextPage}
          />

          {/* Product Type dropdown */}
          <DropdownFilter
            title="Product Type"
            options={[
              {
                items: Object.values(ProductTypeEnum).map((product_type) => {
                  return { label: product_type, value: product_type };
                }),
              },
            ]}
            values={queryStates.product_types || []}
            onChange={(values) => setQueryStates({ product_types: values })}
          />

          {/* Time Period filter */}
          <TimePeriodDropdown
            value={effectiveTimePeriod}
            onChange={(time_period) =>
              setQueryStates({
                time_period,
              })
            }
          />
        </ParamDataTableToolbar>
      </AssistantFilterBar>

      {/* KPI cards */}
      <div className="grid mb-4 grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4">
        <PharmacyMetricCards params={params} />
      </div>

      {/* Charts + Tables */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Left section */}
          <div className="flex flex-col gap-4 lg:col-span-3">
            <SubordersOverTime params={params} />
            <SubordersByPractice params={params} />
          </div>

          {/* Right section */}
          <div className="lg:col-span-2">
            <TopProducts params={params} />
          </div>
        </div>
      </div>
    </div>
  );
}
